import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import APICtx from '#api'
import { TplWSidebar as Tpl } from '#Template'
import DataList from '#DataList'
import { Box, TextField, Typography } from '@mui/material';

import {
	gfxTimer,
} from './Widgets'
import {
	faSlash,
	faChartSimple,
	faEyeSlash,
} from '@fortawesome/pro-regular-svg-icons'
import { DisplayUser } from '#Display';

const drawTimer = time => {
	if(!time || time <= 0)
		return '-'
	return gfxTimer(Math.round(time) / 1000)
}

const drawUsers = users => users?.map(
	user => (
		<Typography>
			<DisplayUser uid={user} />
		</Typography>
	),
)

const DisplayResource = ({ gid }) => {
	const [ group, setGroup ] = useState({})
	const api = useContext(APICtx)

	useEffect(() => {
		gid && api.auth.appGroupGet(gid)
			.then(setGroup)
	}, [ gid ])

	return group?.displayName || group?.address || ''
}

const drawResources = resources => resources?.map(
	resource => (
		<Typography>
			<DisplayResource gid={resource} />
		</Typography>
	),
)

const tableDef = [
	{ label:"ODL", field:'odlCode' },
	{
		label: "Quantità",
		field: 'quantity',
		type: 'number'
	},
	{
		label: "Qtà aggregata",
		field: 'aggregatedQty',
		type: 'number'
	},
	{
		label: "Tempo preparazione",
		cellAlign: 'right',
		content: row => drawTimer(row?.prepTimerTotal),
	},
	{
		label: "Preparatori",
		content: row => drawUsers(row?.prepUsers),
	},
	{
		label: "Tempo prod. totale",
		cellAlign: 'right',
		content: row => drawTimer(row?.timerTotal),
	},
	{
		label: "Tempo prod. medio (agg.)",
		cellAlign: 'right',
		content: row => {
			const quantity = row?.aggregatedQty || row?.quantity || 0
			return (row?.timerTotal && quantity)
				? drawTimer(Math.round(row.timerTotal * 10000 / quantity))
				: '-'
		},
	},
	{
		label: "Lavoratori",
		content: row => drawUsers(row?.prodUsers),
	},
	{
		label: "Risorse usate",
		content: row => drawResources(row?.resources),
	},
	{
		label: "Data/ora fine",
		field: 'completedTS',
		type: 'datetime',
	},
	{
		label: "Statistiche?",
		field: 'statsDisabled',
		type: 'boolean',
		trueIcon:faEyeSlash,
		falseIcon:null,
		tooltip: 'Non elaborato nelle statistiche',
	},
]

export default function ODLList() {
	const api = useContext(APICtx)
	const { recipe, phase } = useParams()
	const [ ids, setIds ] = useState([])
	const [ stats, setStats ] = useState([])
	const [ recipeData, setRecipeData ] = useState()

	const handleSummaryRefresh = () =>
		api.call('ops/phase/stats/summary', { phase }).then(summary => {
			const quantity = summary?.quantity ? (summary.quantity / 10000) : 0
			const aggregatedQty = summary?.aggregatedQty ? (summary.aggregatedQty / 10000) : 0
			const count = summary?._count || 0
			const quantityAvg = quantity && count ? quantity / count : 0
			const aggregatedQtyAvg = aggregatedQty && count ? aggregatedQty / count : 0

			const prepTimerTotal = summary?.prepTimerTotal || 0
			const prepTimeAvgMillis = prepTimerTotal && quantity ? prepTimerTotal / quantity : 0
			const prepTimeAggMillis = prepTimerTotal && aggregatedQty ? prepTimerTotal / aggregatedQty : 0
			const prodTimerTotal = summary?.timerTotal || 0
			const prodTimeAvgMillis = prodTimerTotal && quantity ? prodTimerTotal / quantity : 0
			const prodTimeAggMillis = prodTimerTotal && aggregatedQty ? prodTimerTotal / aggregatedQty : 0

			const prepAvgTime = prepTimeAvgMillis > 0
				? Math.round(prepTimeAvgMillis) / 1000
				: 0
			const prepAggTime = prepTimeAggMillis > 0
				? Math.round(prepTimeAggMillis) / 1000
				: 0
			const prodAvgTime = prodTimeAvgMillis > 0
				? Math.round(prodTimeAvgMillis) / 1000
				: 0
			const prodAggTime = prodTimeAggMillis > 0
				? Math.round(prodTimeAggMillis) / 1000
				: 0
			setStats({
				quantityAvg, aggregatedQtyAvg,
				prepAvgTime, prepAggTime,
				prodAvgTime, prodAggTime,
			})
		})
	const handleRefresh = () => Promise.all([
		handleSummaryRefresh(),
		api.call('ops/phase/stats/list', { phase }).then(setIds),
	])
	useEffect(() => {
		handleRefresh()
		api.call('ops/recipe/get', { _id:recipe }).then(setRecipeData)
	}, [])

	const handleStatsEnable = ({ _id, handleRowRefresh }) =>
		api.call('ops/phase/stats/enable', { _id })
			.then(() => handleRowRefresh() && handleSummaryRefresh())
	const handleStatsDisable = ({ _id, handleRowRefresh }) =>
		api.call('ops/phase/stats/disable', { _id })
			.then(() => handleRowRefresh() && handleSummaryRefresh())

	const getRow = _id => api.call('ops/phase/stats/get', { _id })
	const defBtns = [
		{
			icon: faChartSimple,
			icon2: faSlash,
			label: 'Rimuovi dalle statistiche',
			onClick: handleStatsDisable,
			show: row => !row.statsDisabled,
		},
		{
			icon: faChartSimple,
			label: 'Mostra nelle statistiche',
			onClick: handleStatsEnable,
			show: row => !!row.statsDisabled,
		},
	]

	const title = (recipeData?.label ? recipeData.label+' - ' : '') + 'Riepilogo tempi'
	return (
		<Tpl title={title}>
			<Box>
				<Typography variant='h6' sx={{ mb:1 }}>Statistiche:</Typography>
				<TextField sx={{ mb:1 }} label="Quantità media" variant="outlined" value={stats.quantityAvg || ''} disabled fullWidth />
				<TextField sx={{ mb:1 }} label="Tempo medio prep. per pezzo" variant="outlined" value={gfxTimer(stats.prepAvgTime) || ''} disabled fullWidth />
				<TextField sx={{ mb:1 }} label="Tempo medio prod. per pezzo" variant="outlined" value={gfxTimer(stats.prodAvgTime) || ''} disabled fullWidth />
				<TextField sx={{ mb:1 }} label="Tempo medio prep. per pezzo aggregato" variant="outlined" value={gfxTimer(stats.prepAggTime) || ''} disabled fullWidth />
				<TextField sx={{ mb:1 }} label="Tempo medio prod. per pezzo aggregato" variant="outlined" value={gfxTimer(stats.prodAggTime) || ''} disabled fullWidth />
			</Box>
			<DataList def={tableDef} rowActions={defBtns} ids={ids} getRow={getRow} pageSize={30} />
		</Tpl>
	)
}
